<script setup lang="ts">
import { ref, watch } from "vue";

import { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionCategoryId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";

import SiteSectionsTreeSectionCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  sectionCategoryFilterOptions: SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory?: (name: string) => Promise<boolean>;
  isCreatingSectionCategory?: boolean;
  initialSelection: SiteSectionCategoryId[];
}>();

const emits = defineEmits<{
  (e: "submit-selection", selectedSectionCategoryIds: SiteSectionCategoryId[]): void;
}>();

const { t, k } = useTypedIntl();

const displayedSelectedSectionCategoryIds = ref<SiteSectionCategoryId[]>([]);

function handleCancelSelection(callback?: () => void) {
  displayedSelectedSectionCategoryIds.value = props.initialSelection;
  callback?.();
}

function handleSubmitSectionCategorySelection(callback: () => void) {
  emits("submit-selection", displayedSelectedSectionCategoryIds.value);
  callback();
}

watch(
  () => props.initialSelection,
  (nextSelection) => {
    displayedSelectedSectionCategoryIds.value = [...nextSelection];
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <SiteSectionsTreeSectionCategoryFilter
    v-model:selected-categories="displayedSelectedSectionCategoryIds"
    :options="props.sectionCategoryFilterOptions"
    :create-category="props.createCategory"
    :is-creating-category="props.isCreatingSectionCategory"
    show-create-category
    @hide="handleCancelSelection"
  >
    <template #footer="{ hideSelectOptions }">
      <div class="flex gap-2 p-4 border-t w-full">
        <HospitalityButton
          class="flex flex-grow"
          :data-test-id="DATA_TEST_IDS.components.siteConsumptionMonitoringSectionCategoryFilter.cancelButton"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          size="xs"
          @click="handleCancelSelection(hideSelectOptions)"
        />
        <HospitalityButton
          class="flex flex-grow"
          :data-test-id="DATA_TEST_IDS.components.siteConsumptionMonitoringSectionCategoryFilter.submitButton"
          :label="t(k.global.misc.submit)"
          size="xs"
          @click="handleSubmitSectionCategorySelection(hideSelectOptions)"
        />
      </div>
    </template>
  </SiteSectionsTreeSectionCategoryFilter>
</template>
